export const FormFieldSpecialType = {
    SessionTypeName: 3,
    SessionDateTime: 5,
    SessionPeriod: 21
};

export type DateOccupancy = {
    date: string;
    occupancy: string;
};

export function isSlotSessionType(sessionTypeName?: string | null) {
    const n = (sessionTypeName ?? "").trim().toLowerCase();
    return n.includes("half day") || n.includes("short session");
}

export function allowsSplitSlots(schoolTypeName?: string | null) {
    const t = (schoolTypeName ?? "").toString().toLowerCase().replace(/[\s-_]/g, "");
    return t === "primary" || t === "postprimary";
}

export function normalizePeriod(period?: string | null) {
    const p = (period ?? "").trim().toUpperCase();
    return p === "AM" || p === "PM" ? p : null;
}

export function isVisitDateSelectionReady(
    sessionTypeName?: string | null,
    sessionPeriod?: string | null,
    schoolTypeName?: string | null
) {
    if (!(sessionTypeName ?? "").trim())
        return false;

    if (allowsSplitSlots(schoolTypeName) && isSlotSessionType(sessionTypeName))
        return !!normalizePeriod(sessionPeriod);

    return true;
}

export function toIsoDateValue(value?: string | Date | null) {
    if (!value) return "";
    if (value instanceof Date && !isNaN(value.getTime())) {
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    }
    return String(value).trim().substring(0, 10);
}

export function visitOccupancy(sessionTypeName?: string | null, sessionPeriod?: string | null, schoolTypeName?: string | null) {
    if (!allowsSplitSlots(schoolTypeName) || !isSlotSessionType(sessionTypeName))
        return "Full";
    return normalizePeriod(sessionPeriod) ?? "Full";
}

export function mergeOccupancy(occupancies: Array<string | null | undefined>) {
    const set = new Set((occupancies ?? []).filter(Boolean).map(o => String(o)));
    if (set.has("Full") || (set.has("AM") && set.has("PM")))
        return "Full";
    if (set.has("AM"))
        return "AM";
    if (set.has("PM"))
        return "PM";
    return "Full";
}

export function occupancyConflicts(existingOccupancy: string, incomingOccupancy: string) {
    if (existingOccupancy === "Full" || incomingOccupancy === "Full")
        return true;
    return existingOccupancy === incomingOccupancy;
}

export function isDateDisabledForSelection(
    occupancy: string,
    sessionTypeName?: string | null,
    sessionPeriod?: string | null,
    schoolTypeName?: string | null
) {
    if (!allowsSplitSlots(schoolTypeName) || !isSlotSessionType(sessionTypeName))
        return true;

    const period = normalizePeriod(sessionPeriod);
    if (occupancy === "Full")
        return true;
    if (!period)
        return false;
    return occupancy === period;
}

export function getSubsectionFieldValue(formFieldKeys: any[], subSectionId: any, specialTypeId: number) {
    return (formFieldKeys ?? [])
        .find((ffk: any) => ffk.subSectionId == subSectionId && ffk.formFieldKeySpecialTypeId === specialTypeId)
        ?.formField?.fieldValue ?? "";
}

export function getSiblingOccupancy(
    formFieldKeys: any[],
    currentFieldId: number,
    schoolTypeName?: string | null
): DateOccupancy[] {
    const byDate: Record<string, string[]> = {};

    (formFieldKeys ?? [])
        .filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionDateTime && ffk.id !== currentFieldId)
        .forEach((ffk: any) => {
            const date = toIsoDateValue(ffk.formField?.fieldValue);
            if (!date) return;
            const occupancy = visitOccupancy(
                getSubsectionFieldValue(formFieldKeys, ffk.subSectionId, FormFieldSpecialType.SessionTypeName),
                getSubsectionFieldValue(formFieldKeys, ffk.subSectionId, FormFieldSpecialType.SessionPeriod),
                schoolTypeName
            );
            if (!byDate[date]) byDate[date] = [];
            byDate[date].push(occupancy);
        });

    return Object.keys(byDate).map(date => ({
        date,
        occupancy: mergeOccupancy(byDate[date])
    }));
}

export function getExtraDisabledDatesForVisit(
    formFieldKeys: any[],
    currentField: any,
    schoolTypeName?: string | null
) {
    const sessionTypeName = getSubsectionFieldValue(formFieldKeys, currentField?.subSectionId, FormFieldSpecialType.SessionTypeName);
    const sessionPeriod = getSubsectionFieldValue(formFieldKeys, currentField?.subSectionId, FormFieldSpecialType.SessionPeriod);
    return getSiblingOccupancy(formFieldKeys, currentField?.id, schoolTypeName)
        .filter(item => isDateDisabledForSelection(item.occupancy, sessionTypeName, sessionPeriod, schoolTypeName))
        .map(item => item.date);
}

export function isPeriodOptionDisabled(
    lookupValue: string,
    visitDate?: string | null,
    occupancy: DateOccupancy[] = [],
    siblingOccupancy: DateOccupancy[] = []
) {
    const date = toIsoDateValue(visitDate);
    if (!date) return false;

    const existing = [...occupancy, ...siblingOccupancy]
        .filter(item => toIsoDateValue(item.date) === date)
        .map(item => item.occupancy);

    if (!existing.length) return false;

    const merged = mergeOccupancy(existing);
    if (merged === "Full") return true;
    return merged === normalizePeriod(lookupValue);
}

export function parseBookedDatesResponse(json: any): { disabledDates: Array<string | Date>; occupancy: DateOccupancy[] } {
    if (Array.isArray(json)) {
        return { disabledDates: json, occupancy: [] };
    }

    return {
        disabledDates: Array.isArray(json?.disabledDates) ? json.disabledDates : [],
        occupancy: (json?.occupancy ?? []).map((item: any) => ({
            date: toIsoDateValue(item.date ?? item.Date),
            occupancy: item.occupancy ?? item.Occupancy ?? "Full"
        }))
    };
}
